import { useEffect, useRef, useState, useCallback } from 'react';

// --- Configuration -----------------------------------------------------
// In a real app these would come from env vars / props, not hardcoded.
const SIGNALING_URL = process.env.REACT_APP_SIGNALING_URL || 'ws://localhost:8080';
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

// Connection lifecycle states, used to drive the UI.
const STATUS = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  LIVE: 'live',
  ERROR: 'error',
  CLOSED: 'closed',
};

export function Broadcaster({ roomId = 'my-room' }) {
  const videoRef = useRef(null);

  // Refs (not state) for mutable objects that shouldn't trigger re-renders.
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  // ICE candidates can arrive before the remote description is set.
  // We buffer them here and flush once setRemoteDescription resolves.
  const pendingCandidatesRef = useRef([]);

  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);

  // Tears down the socket, peer connection, and camera/mic tracks.
  // Safe to call multiple times (e.g. on error, on unmount, on manual stop).
  const cleanup = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;

    wsRef.current?.close();
    wsRef.current = null;

    // Release the camera/mic — otherwise the browser keeps the
    // "recording" indicator on and the device stays locked.
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;

    pendingCandidatesRef.current = [];

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startBroadcast = useCallback(async () => {
    setStatus(STATUS.CONNECTING);
    setErrorMessage(null);

    try {
      // Ask the user for camera + mic access. Swap for
      // navigator.mediaDevices.getDisplayMedia({ video: true }) to
      // broadcast a screen share instead of the camera.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = pc;

      // Attach every local track (video + audio) so it's included
      // in the offer sent to the viewer.
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const ws = new WebSocket(SIGNALING_URL);
      wsRef.current = ws;

      // Fires whenever the browser discovers a new possible network path
      // (local IP, public IP via STUN, or a TURN relay). Each one must be
      // forwarded to the viewer through the signaling server.
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          ws.send(JSON.stringify({ type: 'candidate', candidate: event.candidate }));
        }
      };

      // Surface connection drops (network loss, viewer left, etc.).
      pc.oniceconnectionstatechange = () => {
        if (['disconnected', 'failed', 'closed'].includes(pc.iceConnectionState)) {
          setStatus(STATUS.CLOSED);
        }
      };

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'join', room: roomId }));
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            // A viewer joined and is asking us to start the handshake.
            case 'request-offer': {
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              ws.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription }));
              break;
            }

            case 'answer': {
              await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));

              // Now that the remote description is set, it's safe to
              // apply any ICE candidates that arrived early.
              for (const candidate of pendingCandidatesRef.current) {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              }
              pendingCandidatesRef.current = [];

              setStatus(STATUS.LIVE);
              break;
            }

            case 'candidate': {
              if (pc.remoteDescription) {
                await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
              } else {
                // Remote description isn't set yet — buffer for later.
                pendingCandidatesRef.current.push(message.candidate);
              }
              break;
            }

            default:
              // Unknown message type — ignore rather than throw, so a
              // future signaling change doesn't crash the broadcaster.
              break;
          }
        } catch (err) {
          setErrorMessage('Failed to process signaling message.');
          setStatus(STATUS.ERROR);
        }
      };

      ws.onerror = () => {
        setErrorMessage('Signaling connection error.');
        setStatus(STATUS.ERROR);
      };

      ws.onclose = () => {
        setStatus((prev) => (prev === STATUS.LIVE ? STATUS.CLOSED : prev));
      };
    } catch (err) {
      // Most commonly: user denied camera/mic permission, or no
      // device is available.
      setErrorMessage('Could not access camera/microphone.');
      setStatus(STATUS.ERROR);
      cleanup();
    }
  }, [roomId, cleanup]);

  const stopBroadcast = useCallback(() => {
    cleanup();
    setStatus(STATUS.IDLE);
  }, [cleanup]);

  // Always clean up on unmount to avoid leaking connections and
  // leaving the camera light on.
  useEffect(() => cleanup, [cleanup]);

  const isBroadcasting = status === STATUS.CONNECTING || status === STATUS.LIVE;

  return (
    <div className="broadcaster">
      <h2>Broadcaster</h2>

      {/* muted: prevents hearing your own mic played back to yourself */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="broadcaster__video"
      />

      <p className="broadcaster__status">Status: {status}</p>
      {errorMessage && <p className="broadcaster__error">{errorMessage}</p>}

      <div className="broadcaster__controls">
        {!isBroadcasting ? (
          <button onClick={startBroadcast}>Start Broadcast</button>
        ) : (
          <button onClick={stopBroadcast}>Stop Broadcast</button>
        )}
      </div>
    </div>
  );
}
export default Broadcaster;

